import React, { useEffect, useRef, useState } from 'react'

import { Data2d, ScrollStatus } from 'smooth-scrollbar/interfaces'

import * as S from './styles'
import { TextHomeContainer } from '../textHomeContainer'
import { NhShape } from '@/assets/imgs'
import { Natom } from '@/components'
import { HomePage } from '@/service/api/home'
import { useSmoothScroll } from '@/store/smoothScroll'

type NhShapeContainerProps = {
  data: HomePage['textHome']
}
export const NhShapeContainer: React.FC<NhShapeContainerProps> = ({
  data,
}: NhShapeContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const natomRef = useRef<HTMLDivElement>(null)
  const pointRef = useRef({ x: 0, y: 0, scale: 1, desiredScale: 150 })
  const natomPointRef = useRef({ scale: 1, shrinkTreshold: 0.8 })
  const onWhitePageRef = useRef(false)
  const isScrollbarInteracted = useRef(false)
  const [isVisible, setIsVisible] = useState(false)

  const [activeScroll, setActiveScroll] = useState(false)

  const { defaultElement, getScrollbar } = useSmoothScroll()

  const defaultOffset = -38

  const blockedScroll = useRef(false)

  useEffect(() => {
    const scrollbar = getScrollbar(document.getElementById(defaultElement))
    const container = containerRef.current
    const natom = natomRef.current
    const img = imgRef.current
    const point = pointRef.current
    const natomPoint = natomPointRef.current

    if (!container || !img || !scrollbar || !natom) return

    scrollbar.track.yAxis.element.addEventListener('mousedown', () => {
      isScrollbarInteracted.current = true
    })

    window.addEventListener('mouseup', () => {
      isScrollbarInteracted.current = false
    })

    const handleContainerScroll = (
      delta: Data2d,
      event?: WheelEvent | TouchEvent,
    ) => {
      if (blockedScroll.current && event) {
        event.preventDefault()
        return
      }

      const { y: deltaY } = delta
      const aproxLogoPillarSize = img.width / 10
      const scaleDiff = window.innerWidth / aproxLogoPillarSize
      point.desiredScale = scaleDiff * 1.2 // safety offset

      if (natom.classList.contains('visible')) {
        onWhitePageRef.current = true
      } else {
        onWhitePageRef.current = false
      }

      const getNhShapeScale = () => {
        const dampening = 6 // increases scroll resistance

        const factor = scaleDiff / point.desiredScale / dampening

        const newScale = point.scale + deltaY * factor
        if (newScale <= 1) {
          return 1
        }
        // automate animation after 1/3 of it is complete
        if (newScale >= point.desiredScale / 3) return point.desiredScale

        return newScale
      }

      const getTranslate = () => {
        // amount in pixels to be translated if the image had its initial size
        const defaultSizeTranslateX = 46.2 // px
        const defaultSizeTranslateY = -55 // px

        const translateX = point.scale * defaultSizeTranslateX
        const translateY = point.scale * defaultSizeTranslateY

        if (point.scale > 1.5) {
          return `${translateX},${translateY}`
        }

        return `0,0` // initial position
      }

      const getNatomScale = () => {
        const dampening = 2.5
        const newScale =
          natomPoint.scale +
          deltaY / natom.getBoundingClientRect().width / dampening

        if (newScale <= natomPoint.shrinkTreshold) return 0.001
        if (newScale >= 1) return 1

        return newScale
      }

      // using a lower value or zero might result in flickering
      const defaultRotate = '0.0001,0.0001'
      const defaultTranslate = '0.0001,0.0001'

      if (!onWhitePageRef.current) {
        point.scale = getNhShapeScale()
        const translate = getTranslate()
        const nhShapeScaleUpMatrix = `matrix(${point.scale},${defaultRotate},${point.scale},${translate})`
        img.style.transform = nhShapeScaleUpMatrix

        if (point.scale >= point.desiredScale) {
          blockedScroll.current = true

          natomPoint.scale = 1
          natom.classList.add('visible', 'scaleup')
          const natomScaleUpMatrix = `matrix(${natomPoint.scale},${defaultRotate},${natomPoint.scale},${defaultTranslate})`
          natom.style.transform = natomScaleUpMatrix
          setTimeout(() => {
            setIsVisible(true)
          }, 1000)
          setTimeout(() => {
            blockedScroll.current = false
            natom.classList.remove('scaleup')
          }, 1500)
        }
      } else {
        natomPoint.scale = getNatomScale()
        const natomScaleDownMatrix = `matrix(${natomPoint.scale},${defaultRotate},${natomPoint.scale},${defaultTranslate})`
        natom.style.transform = natomScaleDownMatrix

        if (natomPoint.scale <= natomPoint.shrinkTreshold) {
          blockedScroll.current = true

          setTimeout(() => {
            point.scale = 1
            natom.classList.remove('visible')
            setIsVisible(false)
            const nhShapeScaleDownMatrix = `matrix(${point.scale},${defaultRotate},${point.scale},${defaultTranslate})`
            img.style.transform = nhShapeScaleDownMatrix
            setTimeout(() => {
              blockedScroll.current = false
            }, 1000)
          }, 1000)
        }
      }
    }

    const handleScrollbarScroll = (status: {
      limit: Data2d
      offset: Data2d
    }) => {
      if (!isScrollbarInteracted.current) return

      const { offset } = status
      const { y: scrollY } = offset

      const isOnTopWithoutEndAnimation =
        scrollY < container.offsetTop && point.scale !== 1
      if (isOnTopWithoutEndAnimation) {
        handleContainerScroll({ x: 0, y: -120 })
      }
      const isAtBottomWithoutEndAnimation =
        scrollY > container.offsetTop && point.scale !== point.desiredScale
      if (isAtBottomWithoutEndAnimation) {
        handleContainerScroll({ x: 0, y: 120 })
      }
    }

    const handleWindowScroll = () => {
      const { y: scrollY } = scrollbar.offset

      const scrollListener = (status: ScrollStatus) => {
        handleScrollbarScroll(status)
      }

      const isOnContainerViewPort =
        scrollY >= container.offsetTop - 50 &&
        scrollY <= container.offsetTop + 100

      if (isOnContainerViewPort) {
        scrollbar?.updatePluginOptions('nhShapeAnimation', {
          handleContainerScroll,
        })
        scrollbar.addListener(scrollListener)

        setActiveScroll(true)
      } else {
        scrollbar?.updatePluginOptions('nhShapeAnimation', {
          handleContainerScroll: null,
        })
        scrollbar.removeListener(scrollListener)
        setActiveScroll(false)
      }

      if (blockedScroll.current) {
        scrollbar.scrollTo(0, container.offsetTop)
      }

      const isOnTopWithoutEndAnimation =
        scrollY < container.offsetTop && point.scale !== 1
      if (isOnTopWithoutEndAnimation) {
        scrollbar?.scrollTo(0, container.offsetTop)
        return
      }

      const isAtBottomWithoutEndAnimation =
        scrollY > container.offsetTop && point.scale !== point.desiredScale
      if (isAtBottomWithoutEndAnimation) {
        scrollbar?.scrollTo(0, container.offsetTop)
      }
    }

    scrollbar?.addListener(handleWindowScroll)

    // eslint-disable-next-line consistent-return
    return () => {
      scrollbar?.removeListener(handleWindowScroll)
    }
  }, [defaultElement, getScrollbar])

  return (
    <S.FlexContainerNhShape
      isVisible={isVisible}
      style={{
        pointerEvents: activeScroll ? 'auto' : 'none',
      }}
      ref={containerRef}
    >
      <S.NatomContainer ref={natomRef}>
        <Natom />
      </S.NatomContainer>
      <S.ImageWrapper isVisible={isVisible}>
        <S.NhImage
          offset={defaultOffset}
          ref={imgRef}
          src={NhShape}
          alt="Imagem que representa a nata house"
          width={0}
          height={0}
        />
      </S.ImageWrapper>
      <TextHomeContainer data={data} isVisible={isVisible} />
    </S.FlexContainerNhShape>
  )
}
